/**
 * IMEI validation utilities.
 *
 * Real IMEIs are exactly 15 digits and pass the Luhn checksum.
 * Other codes on the device box (SIM/ICCID, SN, GS1 GTIN barcodes) do not.
 */

const isProduction = window.location.hostname === 'ccm.phanova.com';

export function isValidImei(value: string): boolean {
  if (!/^\d{15}$/.test(value)) return false;

  // Skip Luhn checksum on staging/dev to allow testing with random 15-digit codes
  if (!isProduction) return true;

  // Luhn checksum over all 15 digits (the last digit is the check digit).
  // Double every second digit starting at index 1; if the result > 9 subtract 9.
  // Valid when the total sum is divisible by 10.
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let digit = parseInt(value[i], 10);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
}

export const IMEI_RESCAN_MESSAGE =
  'That does not look like a valid IMEI — you may have scanned the SIM, serial, or barcode number. Please rescan the 15-digit code printed under the IMEI label.';

/**
 * PHA-1103: SmartMeter device ids are not IMEIs.
 *
 * The vendor joins readings on `device_id`, not `imei`, and its glucose meters
 * use 7-digit ids while its blood-pressure cuffs use 15-digit ones. isValidImei
 * rejects a 7-digit id on the length test alone (which, unlike the Luhn step, is
 * not dev-skipped), so validating a SmartMeter registration with it would block
 * every glucose meter in the fleet before the request ever left the browser.
 *
 * There is no checksum to verify here — this is a shape check. The real
 * existence check is the vendor's /api/devices/validate, run server-side at
 * registration.
 *
 * Mirrors bff-server/tellihealth/imei.js; keep the two in step.
 */
export const SMARTMETER_MANUFACTURER = 'SmartMeter';
export const TELLI_MANUFACTURER = 'Telli-Health';

/** Manufacturers offered in the device registration forms. */
export const DEVICE_MANUFACTURERS = [TELLI_MANUFACTURER, SMARTMETER_MANUFACTURER];

export function isValidSmartMeterDeviceId(value: string): boolean {
  return /^\d{6,20}$/.test(value || '');
}

export const SMARTMETER_DEVICE_ID_MESSAGE =
  'That does not look like a valid SmartMeter device id. Use the device id printed on the device — 15 digits for a blood pressure cuff, 7 for a glucose meter.';

/** Validate a device id against the rules for its manufacturer. */
export function isValidDeviceId(value: string, manufacturer?: string): boolean {
  return manufacturer === SMARTMETER_MANUFACTURER
    ? isValidSmartMeterDeviceId(value)
    : isValidImei(value);
}

export function deviceIdMessage(manufacturer?: string): string {
  return manufacturer === SMARTMETER_MANUFACTURER ? SMARTMETER_DEVICE_ID_MESSAGE : IMEI_RESCAN_MESSAGE;
}

/** Field label for the id input, which is only an "IMEI" for Tellihealth. */
export function deviceIdLabel(manufacturer?: string): string {
  return manufacturer === SMARTMETER_MANUFACTURER ? 'Device ID' : 'IMEI (Device ID)';
}
